import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnDestroy,
  ViewChild,
  ViewContainerRef
} from '@angular/core';
import { WorkshopJob } from '../../../models/workshop-job';
import { CustomerEquipment } from '../../../models/customer-equipment';
import { Item } from '../../../models';
import { RepairDocket } from '../../../models/repair-docket';
import { MatDialog } from '@angular/material/dialog';
import { WorkshopJobHistoryComponent } from '../../../shared/dialogs/workshop-job-history/workshop-job-history.component';
import { WORKSHOP } from '../../../../testIds';
import { Subject, takeUntil, timer } from 'rxjs';
import { WorkshopJobService } from 'src/app/shared/services/workshop-job.service';
import { base64ToBlob } from 'src/app/shared/helpers/base64ToBlob';
import { EditWorkshopJobComponent } from '../edit-workshop-job/edit-workshop-job.component';
import { ViewWorkshopJobStoreService } from '../view-workshop-job-store.service';
import { ItemDialogComponent } from 'src/app/shared/dialogs/item-dialog/item-dialog.component';
import { WorkshopJobStatusLabelPipe } from '../../../shared/pipes/workshop-job-status-label.pipe';
import { RouterLink } from '@angular/router';
import { NgIf } from '@angular/common';
import { KeyValuePairComponent } from '../../../shared/general/key-value-pair/key-value-pair.component';
import { MatIcon } from '@angular/material/icon';
import { MatIconButton, MatButton } from '@angular/material/button';

@Component({
    selector: 'app-workshop-job-details',
    templateUrl: './workshop-job-details.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [MatIconButton, MatIcon, MatButton, KeyValuePairComponent, NgIf, RouterLink, WorkshopJobStatusLabelPipe]
})
export class WorkshopJobDetailsComponent implements OnDestroy {
  @Input() job: WorkshopJob<any>;
  @Input() repairDocket: RepairDocket;
  @Input() templateName: string | null;
  testIds = WORKSHOP;
  @ViewChild('printFrame') printFrame;
  ngUnsubscribe = new Subject<void>();

  constructor(
    private vcr: ViewContainerRef,
    private dialog: MatDialog,
    private workshopJobService: WorkshopJobService,
    private store: ViewWorkshopJobStoreService
  ) {}

  get jobName() {
    if (this.job.repairable_type === 'customerEquipment') {
      return `${this.job.type} of ${
        (this.job.repairable as CustomerEquipment).name
      }`;
    }
    return `${this.job.type} of ${(this.job.repairable as Item).code}`;
  }

  get serialNumber() {
    if (this.job.repairable_type === 'item') {
      return null;
    }
    return (this.job.repairable as CustomerEquipment).serial_number;
  }

  openHistoryDialog() {
    if (this.job.repairable_type === 'item') {
      this.dialog.open(ItemDialogComponent, {
        data: {
          item: this.job.repairable as Item
        }
      });
      return;
    }

    this.dialog.open(WorkshopJobHistoryComponent, {
      data: {
        type: this.job.repairable_type,
        id: this.job.repairable_id,
        repairable: this.job.repairable
      },
      minWidth: '50vh',
      viewContainerRef: this.vcr
    });
  }

  edit() {
    this.dialog
      .open(EditWorkshopJobComponent, {
        data: this.job,
        viewContainerRef: this.vcr
      })
      .afterClosed()
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((result) => {
        if (result?.update) {
          this.store.loadJob(this.job.id);
        }
      });
  }

  print(download = false) {
    this.workshopJobService
      .print(this.job.id)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((response) => {
        let src = URL.createObjectURL(
          base64ToBlob(response, 'application/pdf')
        );
        if (download) {
          const link = document.createElement('a') as HTMLAnchorElement;
          link.href = src;
          const ref = `Workshop Job ${this.job.id}`;
          link.download = `${ref}.pdf`;
          link.click();
        } else {
          this.printFrame.nativeElement.src = src;
          this.printIframe();
        }
      });
  }

  printIframe() {
    timer(500)
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe(() => {
        this.printFrame.nativeElement.contentWindow.print();
      });
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
